import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Theme,
  Typography,
} from "@mui/material";
import type { ChangeEvent, Dispatch, MouseEvent } from "react";

import { ActivitySortField, GetActivitiesQuery } from "../../../../../../graphql/graphql";
import {
  formatEnumLabel,
  planTableToggleAriaLabel,
  sortOrderToMui,
} from "../CreateCallPlanModal.utils";
import {
  ActivitiesQueryAction,
  ActivitiesQueryState,
} from "./ActivitiesTable.activitiesQueryState";
import {
  activitiesTableLoadingOverlaySx,
  addActivityBodyCellSx,
  addActivityHeaderCellSx,
  categoryPillSx,
  neutralPillFilterMatchSx,
  neutralPillSx,
  paginationContainerSx,
  pillClusterBodyCellSx,
  pillClusterHeaderCellSx,
  pillContainerSx,
  planAddButtonBlockedSx,
  planAddButtonEnabledSx,
  planRemoveButtonSx,
  planToggleGlyphSx,
  tableBodyCellSx,
  tableContainerSx,
  tableHeaderCellSx,
  tablePaginationSx,
  tableScrollAreaSx,
  tableWrapperSx,
} from "./ActivitiesTable.styles";

type ActivityRow = GetActivitiesQuery["activities"]["activities"][number];

interface ActivitiesTableProps {
  activitiesLoading: boolean;
  activities: ActivityRow[];
  totalActivities: number;
  queryState: ActivitiesQueryState;
  dispatchQuery: Dispatch<ActivitiesQueryAction>;
  typeKeysSelected: Record<string, boolean>;
  onPageChange: (_: MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  onRowsPerPageChange: (event: ChangeEvent<HTMLInputElement>) => void;
  activityIdsSelected: Record<number, boolean>;
  atCapacity: boolean;
  onAddActivity: (activity: ActivityRow) => void;
  onRemoveActivity: (activityId: number) => void;
}

interface ActivityTableColumnSortLabelProps {
  column: ActivitySortField;
  queryState: ActivitiesQueryState;
  dispatchQuery: Dispatch<ActivitiesQueryAction>;
}

const ActivityTableColumnSortLabel: React.FC<ActivityTableColumnSortLabelProps> = ({
  column,
  queryState,
  dispatchQuery,
}) => (
  <TableSortLabel
    active={queryState.sortField === column}
    direction={sortOrderToMui(queryState.sortDirection)}
    onClick={() => dispatchQuery({ type: "TOGGLE_SORT", field: column })}
  >
    {formatEnumLabel(column)}
  </TableSortLabel>
);

const ActivitiesTable: React.FC<ActivitiesTableProps> = ({
  activitiesLoading,
  activities,
  totalActivities,
  queryState,
  dispatchQuery,
  typeKeysSelected,
  onPageChange,
  onRowsPerPageChange,
  activityIdsSelected,
  atCapacity,
  onAddActivity,
  onRemoveActivity,
}) => (
  <Box sx={tableWrapperSx}>
    <Box sx={tableScrollAreaSx}>
      <TableContainer sx={tableContainerSx}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={addActivityHeaderCellSx} aria-label="Add to plan" />
              <TableCell sx={tableHeaderCellSx}>
                <ActivityTableColumnSortLabel
                  column={ActivitySortField.Title}
                  queryState={queryState}
                  dispatchQuery={dispatchQuery}
                />
              </TableCell>
              <TableCell sx={tableHeaderCellSx}>
                <ActivityTableColumnSortLabel
                  column={ActivitySortField.Category}
                  queryState={queryState}
                  dispatchQuery={dispatchQuery}
                />
              </TableCell>
              <TableCell sx={pillClusterHeaderCellSx}>Activity Types</TableCell>
              <TableCell sx={tableHeaderCellSx}>
                <ActivityTableColumnSortLabel
                  column={ActivitySortField.Difficulty}
                  queryState={queryState}
                  dispatchQuery={dispatchQuery}
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activities.map((activity) => {
              const alreadyAdded = activityIdsSelected[activity.id];
              const addBlocked = !alreadyAdded && atCapacity;

              return (
                <TableRow key={activity.id} hover>
                  <TableCell sx={addActivityBodyCellSx}>
                    <IconButton
                      size="small"
                      aria-label={planTableToggleAriaLabel(
                        activity.title,
                        alreadyAdded ? "remove" : addBlocked ? "blocked" : "add"
                      )}
                      disabled={addBlocked}
                      onClick={() =>
                        alreadyAdded ? onRemoveActivity(activity.id) : onAddActivity(activity)
                      }
                      sx={
                        alreadyAdded
                          ? planRemoveButtonSx
                          : addBlocked
                          ? planAddButtonBlockedSx
                          : planAddButtonEnabledSx
                      }
                    >
                      <Typography component="span" sx={planToggleGlyphSx}>
                        {alreadyAdded ? "-" : "+"}
                      </Typography>
                    </IconButton>
                  </TableCell>
                  <TableCell sx={tableBodyCellSx}>{activity.title}</TableCell>
                  <TableCell sx={tableBodyCellSx}>
                    <Chip
                      size="small"
                      label={formatEnumLabel(activity.category)}
                      sx={categoryPillSx}
                    />
                  </TableCell>
                  <TableCell sx={pillClusterBodyCellSx}>
                    {activity.activityTypes.length ? (
                      <Box sx={pillContainerSx}>
                        {activity.activityTypes.map((type) => (
                          <Chip
                            key={type.activityTypeKey}
                            size="small"
                            label={type.activityTypeLabel}
                            sx={
                              {
                                ...neutralPillSx,
                                ...(queryState.typeKeys.length > 0 &&
                                typeKeysSelected[type.activityTypeKey]
                                  ? neutralPillFilterMatchSx
                                  : {}),
                              } as SxProps<Theme>
                            }
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2">-</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={tableBodyCellSx}>{formatEnumLabel(activity.difficulty)}</TableCell>
                </TableRow>
              );
            })}
            {!activities.length && !activitiesLoading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No activities found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {activitiesLoading && (
          <Box sx={activitiesTableLoadingOverlaySx}>
            <CircularProgress />
          </Box>
        )}
      </TableContainer>
    </Box>

    <Box sx={paginationContainerSx}>
      <TablePagination
        component="div"
        count={totalActivities}
        page={queryState.page}
        onPageChange={onPageChange}
        rowsPerPage={queryState.pageSize}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[20, 50, 100]}
        showFirstButton
        showLastButton
        sx={tablePaginationSx}
      />
    </Box>
  </Box>
);

export default ActivitiesTable;
